import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "../stores/auth/useAuthStore";
import SvgFragment from "./SvgFragment";
import DropdownMenu from "../components/dropdown/DropdownMenu";
import styled from "styled-components";
import Text from "../components/Text";
import ImageNoNotifications from '../assets/img/no-notifications.png';
import { useNotificationsRequest } from "../services/apis/notifications/useNotificationsRequest";
import ProfilePhoto from "../components/ProfilePhoto";
import Row from "../components/Row";
import moment from "moment";
import DropdownItem from "../components/dropdown/DropdownItem";
import { styles } from "../styles/themes/style";
import { useNotificationsHandlers } from "../services/actions/notifications/useNotificationsHandlers";
import { useAuthActions } from "../stores/auth/useAuthActions";
import { Bell } from 'lucide-react';
import DropdownEmptyState from "../components/DropdownEmptyState";
import { useConnectionsHandlers } from "../services/actions/connections/useConnectionsHandlers";
import { useConnectionsStore } from "../stores/connections/useConnectionsStore";
import { AppRoutes } from "../interfaces/store/appStore";
import { useNavigate } from "react-router-dom";
import ActionText from "../components/ActionText";
import { useAppHandlers } from "../services/actions/app/useAppHandlers";
import Column from "../components/Column";
import { useAppStore } from "../stores/application/useAppStore";
import { useExternalHandlers } from "../services/actions/navigation/useExternalHandlers";
import LoadingSpinner from "../components/LoadingSpinner";

interface INotiicationsProps { }

const Notifications: FunctionComponent<INotiicationsProps> = () => {
    const { store: { user, userWorkspaces, authData: { accessToken, refreshToken } } } = useAuthStore()
    const { store: { requestLoading } } = useAppStore()
    const { setRemovalOverviewModal, setDestructiveModal, hideModal, setHubspotExportResultModalHandler } = useAppHandlers()

    const { setUserWorkspaces } = useAuthActions()
    const { markAsReadHandler, deleteNotificationHandler } = useNotificationsHandlers() 
    const { getNotifications } = useNotificationsRequest() 

    const { visitConnectionHandler } = useExternalHandlers()

    const { store: { connectionsFilter } } = useConnectionsStore()
    const { setConnectionsFilterHandler, cancelConnectionsRemovalHandler, messageClickHandler, getConnectionByPublicIdentifierHandler, visitConnectionAndUpdateHandler } = useConnectionsHandlers()
    const navigate = useNavigate();
    const { store: { scheduledRemovalParameters }} = useConnectionsStore()

    const [notifications, setNotifications] = useState<any>({
        unread: null,
        notifications: null
    })
    const [localNotifications, setLocalNotifications] = useState<any>(null)
    const [closeDropdown, setCloseDropdown] = useState(false);
    const [loadingConversation, setLoadingConversation] = useState<string | null>(null)
    const [deletingNotification, setDeletingNotification] = useState(false)
    // no-op: removed debug effect

    const markAsRead = async (notification: any, closeDropdown: boolean, redirect?: string, ) => {
        let selectedWorkspace = userWorkspaces.find((item: any) => item.selected)

        const updatedNotifications = notifications.notifications.map((item: any) => item._id === notification._id ? { ...item, read: true } : item)

        const unreadNotificationsCount = updatedNotifications.filter((item: any) => !item?.read)?.length

        if(selectedWorkspace) selectedWorkspace.notifications.unread = unreadNotificationsCount

        const updatedUserWorkspaces = userWorkspaces.map((workspace: any) => workspace.isSelected
            ? { ...workspace, notifications: { ...workspace.notifications, unread: unreadNotificationsCount } }
            : workspace
        )

        setLocalNotifications(updatedNotifications)

        setUserWorkspaces(updatedUserWorkspaces)

        setNotifications({
            notifications: updatedNotifications,
            unread: unreadNotificationsCount
        })

        await markAsReadHandler({ ids: [notification?._id] })

        if (closeDropdown) {
            setCloseDropdown(true);
            setTimeout(() => setCloseDropdown(false), 0);
        }
        if (redirect === 'note') navigate(AppRoutes.NOTES, { state: { noteId: notification?.meta?.noteId } })
    }

    const deleteNotification = async (event: any, notification: any) => {
        event.stopPropagation()

        setDeletingNotification(true)

        // Optimistically update UI
        const updatedNotifications = (notifications.notifications || []).filter((item: any) => item?._id !== notification?._id)

        const unreadNotificationsCount = updatedNotifications.filter((item: any) => !item?.read)?.length

        // Update workspace unread counter
        let selectedWorkspace = userWorkspaces.find((item: any) => item.selected)
        if (selectedWorkspace) selectedWorkspace.notifications.unread = unreadNotificationsCount

        const updatedUserWorkspaces = userWorkspaces.map((workspace: any) => workspace.isSelected
            ? { ...workspace, notifications: { ...workspace.notifications, unread: unreadNotificationsCount } }
            : workspace
        )

        setUserWorkspaces(updatedUserWorkspaces)

        setLocalNotifications(updatedNotifications)
        setNotifications({
            notifications: updatedNotifications,
            unread: unreadNotificationsCount
        })

        // Persist deletion
        await deleteNotificationHandler(notification._id)

        setDeletingNotification(false)
    }

    const notificationClickHadler = async (notification: any) => {
        const isRemovalNotification = notification.type === 'schedule-connections-removal'
            || notification.type === 'cancel-connections-removal'
            || notification.type === 'finish-connections-removal'

        if (notification.type === 'job-change-detected' || isRemovalNotification) {
            if (!notification?.read) await markAsRead(notification, false);
        }
    }

    const hubspotExportClickHandler = async (notification: any) => {
        if (!notification?.read) {
            await markAsRead(notification, false);
        }

        const senderFirstName = notification?.senders?.[0]?.firstName || '';
        const senderLastName = notification?.senders?.[0]?.lastName || '';
        const senderName = `${senderFirstName} ${senderLastName}`.trim();

        setHubspotExportResultModalHandler({
            senderName,
            exportType: notification?.meta?.exportType || 'contacts',
            total: notification?.meta?.total ?? 0,
            successful: notification?.meta?.successful ?? 0,
            failed: notification?.meta?.failed ?? 0,
            createdAt: notification?.createdAt,
            failedList: notification?.meta?.failedList,
        });
    }

    const getHubspotExportLabel = (notification: any) => {
        switch (notification?.meta?.exportType) {
            case 'list':
                return 'list export';
            case 'workflow':
                return 'workflow export';
            case 'deals':
                return 'deal export';
            default:
                return 'contact export';
        }
    }

    const formatHubspotNumber = (value: any) => {
        if (typeof value === 'number') return value.toLocaleString();
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
    }

    const getHubspotProcessingLabel = (notification: any) => {
        switch (notification?.meta?.exportType) {
            case 'list':
                return <>{'is adding contacts to a '}<b>HubSpot</b>{' list'}</>;
            case 'workflow':
                return <>{'is enrolling contacts in a '}<b>HubSpot</b>{' workflow'}</>;
            case 'deals':
                return <>{'is creating '}<b>HubSpot</b>{' deals'}</>;
            default:
                return <>{'is exporting contacts into '}<b>HubSpot</b></>;
        }
    }

    const getHubspotCompletedLabel = (notification: any) => {
        const total = formatHubspotNumber(notification?.meta?.total);
        switch (notification?.meta?.exportType) {
            case 'list':
                return <>{'finished adding '}<b>{total}</b>{' contacts to a '}<b>HubSpot</b>{' Segment (list)'}</>;
            case 'workflow':
                return <>{'finished enrolling '}<b>{total}</b>{' contacts in a '}<b>HubSpot</b>{' workflow'}</>;
            case 'deals':
                return <>{'finished creating '}<b>{total}</b>{' '}<b>HubSpot</b>{' deals'}</>;
            default:
                return <>{'finished exporting '}<b>{total}</b>{' contacts to '}<b>HubSpot</b></>;
        }
    }

    const getHubspotSelfProcessingLabel = (notification: any) => {
        const current = formatHubspotNumber(notification?.meta?.current ?? 0);
        const total = formatHubspotNumber(notification?.meta?.total ?? 0);

        switch (notification?.meta?.exportType) {
            case 'list':
                return <>{'Exporting '}<b>{current}</b>{` of `}<b>{total}</b>{' contacts to a '}<b>HubSpot</b>{' Segment (list)'}</>;
            case 'workflow':
                return <>{'Enrolling '}<b>{current}</b>{` of `}<b>{total}</b>{' contacts in a '}<b>HubSpot</b>{' workflow'}</>;
            case 'deals':
                return <>{'Creating '}<b>{current}</b>{` of `}<b>{total}</b>{' '}<b>HubSpot</b>{' deals'}</>;
            default:
                return <>{'Exporting '}<b>{current}</b>{` of `}<b>{total}</b>{' contacts to '}<b>HubSpot</b></>;
        }
    }

    const getHubspotSelfCompletedLabel = (notification: any) => {
        const total = formatHubspotNumber(notification?.meta?.total ?? 0);

        switch (notification?.meta?.exportType) {
            case 'list':
                return <>{'Added '}<b>{total}</b>{' contacts to a '}<b>HubSpot</b>{' Segment (list)'}</>;
            case 'workflow':
                return <>{'Enrolled '}<b>{total}</b>{' contacts in a '}<b>HubSpot</b>{' workflow'}</>;
            case 'deals':
                return <>{'Created '}<b>{total}</b>{' '}<b>HubSpot</b>{' deals'}</>;
            default:
                return <>{'Exported '}<b>{total}</b>{' contacts to '}<b>HubSpot</b></>;
        }
    }

    const fetchNotifications = useCallback(async () => {
        let selectedWorkspace = userWorkspaces?.find((item: any) => item?.selected);

        if (selectedWorkspace && accessToken && refreshToken) {
            let { response } = await getNotifications()

            const updatedNotifications = response.map((notification: any) => ({
                ...notification,
                read: notification.read ?? false,
            }))

            // Merge with any local optimistic state; prefer local read flags when present
            const mergedNotifications = updatedNotifications.map((serverItem: any) => {
                const localItem = localNotifications?.find((ln: any) => ln?._id === serverItem?._id)
                return {
                    ...serverItem,
                    read: (localItem?.read ?? serverItem.read ?? false)
                }
            })

            const unreadNotificationsCount = mergedNotifications.filter((item: any) => !item.read).length;
            setNotifications({
                unread: unreadNotificationsCount,
                notifications: mergedNotifications,
            })
        }
    }, [userWorkspaces, accessToken, refreshToken, localNotifications])

    useEffect(() => {
        if (user) fetchNotifications()
    }, [fetchNotifications])

    // Fetch notifications when tab becomes active
    const { store: { leadDeltaTabIsActive } } = useAppStore()
    const prevTabActive = useRef<boolean>(false)
    
    useEffect(() => {
        // Only fetch when tab transitions from inactive to active
        if (leadDeltaTabIsActive && !prevTabActive.current && user) {
            console.log('Tab became active - fetching notifications')
            fetchNotifications()
        }
        prevTabActive.current = leadDeltaTabIsActive
    }, [leadDeltaTabIsActive, user, fetchNotifications])

    // Periodic polling every 2 minutes
    useEffect(() => {
        if (!user) return

        console.log('Starting notifications polling (2 min interval)')
        
        const intervalId = setInterval(() => {
            console.log('Periodic notifications fetch')
            fetchNotifications()
        }, 2 * 60 * 1000) // 2 minutes

        return () => {
            console.log('Stopping notifications polling')
            clearInterval(intervalId)
        }
    }, [user, fetchNotifications])

    const onCancelConnectionsRemovalHandler = async () => {
        setDestructiveModal({
            headingText: "Are you sure you want to cancel connections removal?",
            descriptionText: `${scheduledRemovalParameters?.count} out of ${scheduledRemovalParameters?.total} connections removed.`,
            confirmBtnTitle: 'Yes, Cancel Removal',
            onCancelHandler: () => hideModal(),
            onConfirmHandler: async () => {
                hideModal()
                cancelConnectionsRemovalHandler();
            }
        })
    }

    const onClickHandler = async(e: any, notification: any) => {
        e.stopPropagation();

        setLoadingConversation(notification?.publicIdentifier)

        const connection = await getConnectionByPublicIdentifierHandler(notification?.publicIdentifier)

        await messageClickHandler(e, connection, undefined, `Congrats on starting your new role as ${notification?.positionTitle} at ${notification?.companyName}! Wishing you lots of success!`)

        setCloseDropdown(true);
        setTimeout(() => setCloseDropdown(false), 0);

        setLoadingConversation(null)
    }

    const visitLinkedInProfile = (e: any, publicIdentifier: string) => {
        e.stopPropagation();
        setCloseDropdown(true);
        setTimeout(() => setCloseDropdown(false), 0);
        setTimeout(async () => {
            const connection = await getConnectionByPublicIdentifierHandler(publicIdentifier);
            if (connection) {
                visitConnectionAndUpdateHandler(connection);
            } else {
                visitConnectionHandler(publicIdentifier);
            }
        }, 100);
    }

    return (
        <StyledNotificationsDropdown $overflowX='hidden' autoClose='outside' forceClose={closeDropdown} onToggle={(open) => { if (!open) setCloseDropdown(false) }} $hideToggle hideArrow title={
            <NotificationBellTrigger>
                <Bell size={16} strokeWidth={2} />
                {notifications.unread > 0 && <NotificationCount>{notifications.unread}</NotificationCount>}
            </NotificationBellTrigger>
        }>
            <div style={{padding: '10px 0px 20px 20px'}}><Text $heading4>Notifications</Text></div>
            {notifications?.notifications?.length ? (<>
                {notifications.notifications.map((notification: any, index: number) => {
                    const sender = notification.senders?.[0].firstName + ' ' + notification.senders?.[0].lastName
                    const isRemovalNotification = notification.type === 'schedule-connections-removal' || notification.type === 'cancel-connections-removal' || notification.type === 'finish-connections-removal'
                    const isJobChangeNotification = notification.type === 'job-change-detected'
                    const isHubspotExportNotification = notification.type === 'hubspot-export'
                    const isHubspotStaticConversationNotification = notification.type === 'hubspot-export-static-conversation'
                    const isHubspotStaticConversationContinuousNotification = notification.type === 'hubspot-export-static-conversation-continuous'
                    const isCurrentUserHubspotSender = Boolean(
                        isHubspotExportNotification && user?._id && notification?.senders?.some((sender: any) => {
                            const senderId = sender?._id;
                            if (!senderId) return false;
                            return senderId?.toString?.() === user._id?.toString?.();
                        })
                    )
 
                    return (
                        <StyledDropdownItem 
                            key={index}
                            $nonHoverable={!isRemovalNotification && !isJobChangeNotification && !isHubspotExportNotification && !isHubspotStaticConversationNotification && !isHubspotStaticConversationContinuousNotification && notification?.read}
                            onClickHandler={() => isRemovalNotification
                                ?   notificationClickHadler(notification)
                                :   isJobChangeNotification
                                    ?   notificationClickHadler(notification)
                                    :   isHubspotExportNotification
                                        ? notification?.meta?.stage === 'processing'
                                            ? undefined
                                            : hubspotExportClickHandler(notification)
                                    :   (isHubspotStaticConversationNotification || isHubspotStaticConversationContinuousNotification)
                                        ? undefined
                                    :   notification?.meta?.noteId && !notification?.read
                                        ? markAsRead(notification, true, 'note')
                                        : undefined
                            }>
                            {(isRemovalNotification) ? (
                                <RemovalRow alignItems gap="10px" style={{ width: '530px' }}>
                                    <Column gap="5px" style={{ minWidth: 0 }}>
                                        <Row alignItems gap="10px">
                                            <SvgFragment type={
                                                notification.type === 'schedule-connections-removal' ? 'clockGrey' :
                                                notification.type === 'cancel-connections-removal' ? 'closeCircle' :
                                                'check'
                                            }/>
                                            <Row gap="5px" alignItems>
                                                <Text>{notification?.meta?.description1}</Text>
                                                <StyledText>
                                                    {notification?.meta?.description2} 
                                                </StyledText>
                                            </Row>
                                        </Row>
                                        <Row alignItems gap="26px">
                                            <Text></Text>
                                            <Row gap="8px" alignItems>
                                                <ActionText onClickHandler={() => setRemovalOverviewModal()}>Removal overview</ActionText>
                                                { scheduledRemovalParameters.inProgress && <ActionText danger onClickHandler={() => onCancelConnectionsRemovalHandler()}>Cancel Removal</ActionText>}
                                            </Row>
                                        </Row>
                                        <Row alignItems gap="26px">
                                            <Text></Text>
                                            <Text $lighter>{moment(notification.createdAt).fromNow()}</Text>
                                        </Row>
                                    </Column>
                                    <RemovalDot>
                                        {!notification?.read && <SvgFragment type="blueDotNotification" />}
                                    </RemovalDot>
                                </RemovalRow>
                            ) : isJobChangeNotification ? (
                                <HoverRow alignItems gap="10px">
                                    <StyledProfilePhoto source={notification.meta?.profilePicture} />
                                    <Column gap="5px">
                                        <StyledText><HoverSpan onClick={(e) => visitLinkedInProfile(e, notification?.meta?.publicIdentifier)}>{`${notification?.meta?.firstName || ''} ${notification?.meta?.lastName || ''}`.trim()}</HoverSpan> has started a new position as <b>{notification?.meta?.positionTitle}</b></StyledText>
                                        <Row alignItems gap="25px">
                                            <StyledText> at <b>{notification?.meta?.companyName}</b></StyledText>
                                        </Row>
                                        <Column alignItems justifyCenter style={{width: '100px', height: '24px'}} onClick={(e) => onClickHandler(e, notification?.meta)}>
                                            {loadingConversation === notification?.meta?.publicIdentifier ? <LoadingSpinner color={styles.colors.primary600}/> : <SvgFragment type="sayCongrats" />}
                                        </Column>
                                        {!deletingNotification && <CloseButton onClick={(event) => deleteNotification(event, notification)}><SvgFragment type="closeNotification" /></CloseButton>}
                                    </Column>
                                    <Dot>
                                        {!notification?.read && <SvgFragment type="blueDotNotification" />}
                                    </Dot>
                                </HoverRow>
                            ) : isHubspotExportNotification ? (
                                <HoverRow alignItems gap="10px">
                                    {notification?.meta?.stage === 'processing' ? (
                                        <StyledCheckMarkBox>
                                            <HubspotProcessingMark>
                                                <LoadingSpinner color={styles.colors.primary600} height="16px" width="16px" />
                                            </HubspotProcessingMark>
                                        </StyledCheckMarkBox>
                                    ) : (
                                        <StyledCheckMarkBox>
                                            <HubspotCheckMark>
                                                <SvgFragment type={'checkmark'} />
                                            </HubspotCheckMark>
                                        </StyledCheckMarkBox>
                                    )}
                                    <Column>
                                        <Row alignItems gap="10px">
                                            <Column gap="4px">
                                                {notification?.meta?.stage === 'processing' ? (
                                                    <>
                                                        <StyledText>
                                                            {isCurrentUserHubspotSender
                                                                ? getHubspotSelfProcessingLabel(notification)
                                                                : <>
                                                                    <b>{`${notification.senders?.[0]?.firstName || ''} ${notification.senders?.[0]?.lastName || ''}`.trim()}</b> {getHubspotProcessingLabel(notification)}
                                                                </>
                                                            }
                                                        </StyledText>
                                                        {!isCurrentUserHubspotSender && (
                                                            <Text $lighter $paragraph3>
                                                                Exported {formatHubspotNumber(notification?.meta?.current ?? 0)} of {formatHubspotNumber(notification?.meta?.total ?? 0)}
                                                            </Text>
                                                        )}
                                                        <Text $lighter>{moment(notification.createdAt).fromNow()}</Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <StyledText>
                                                            {isCurrentUserHubspotSender
                                                                ? getHubspotSelfCompletedLabel(notification)
                                                                : <>
                                                                    <b>{`${notification.senders?.[0]?.firstName || ''} ${notification.senders?.[0]?.lastName || ''}`.trim()}</b> {getHubspotCompletedLabel(notification)}
                                                                </>
                                                            }
                                                        </StyledText>
                                                        <ActionText $paragraph3 onClickHandler={(event) => { event.stopPropagation(); hubspotExportClickHandler(notification); }}>Export overview</ActionText>
                                                        <Text $lighter>{moment(notification.createdAt).fromNow()}</Text>
                                                    </>
                                                )}
                                            </Column>
                                        </Row>
                                    </Column>
                                    {!deletingNotification && notification?.meta?.stage !== 'processing' && <CloseButton onClick={(event) => deleteNotification(event, notification)}><SvgFragment type="closeNotification" /></CloseButton>}
                                    <Dot>
                                        {!notification?.read && <SvgFragment type="blueDotNotification" />}
                                    </Dot>
                                </HoverRow>
                            ) : (isHubspotStaticConversationNotification || isHubspotStaticConversationContinuousNotification) ? (
                                <HoverRow alignItems gap="10px">
                                    {notification?.meta?.status === 'in_progress' ? (
                                        <StyledCheckMarkBox>
                                            <HubspotProcessingMark>
                                                <LoadingSpinner color={styles.colors.primary600} height="16px" width="16px" />
                                            </HubspotProcessingMark>
                                        </StyledCheckMarkBox>
                                    ) : (
                                        <StyledCheckMarkBox>
                                            <HubspotCheckMark>
                                                <SvgFragment type={'checkmark'} />
                                            </HubspotCheckMark>
                                        </StyledCheckMarkBox>
                                    )}
                                    <Column>
                                        <Row alignItems gap="10px">
                                            <Column gap="4px">
                                                <StyledText>
                                                    {notification?.meta?.status === 'in_progress' 
                                                        ? isHubspotStaticConversationContinuousNotification
                                                            ? <>New messages being added to <b>HubSpot</b> conversation with <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b></>
                                                            : <>Exporting messages to <b>HubSpot</b> conversation with <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b>{notification?.meta?.pendingCount ? `. Currently in queue - ${notification?.meta?.pendingCount}` : ''}</>
                                                        : notification?.meta?.status === 'completed'
                                                            ? isHubspotStaticConversationContinuousNotification
                                                                ? <>New messages exported to <b>HubSpot</b> conversation with <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b></>
                                                                : <>Exported conversation to <b>HubSpot</b> with <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b></>
                                                            : notification?.meta?.status === 'failed'
                                                                ? <>Export to <b>HubSpot</b> failed for <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b></>
                                                                : <>Export to <b>HubSpot</b> - <b>{notification?.meta?.firstName || ''} {notification?.meta?.lastName || ''}</b></>
                                                    }
                                                </StyledText>
                                                <Text $lighter>{moment(notification.createdAt).fromNow()}</Text>
                                            </Column>
                                        </Row>
                                    </Column>
                                    {!deletingNotification && notification?.meta?.status !== 'in_progress' && <CloseButton onClick={(event) => deleteNotification(event, notification)}><SvgFragment type="closeNotification" /></CloseButton>}
                                    <Dot>
                                        {!notification?.read && <SvgFragment type="blueDotNotification" />}
                                    </Dot>
                                </HoverRow>
                            ) : (
                                <Row gap="10px">
                                    <Column>
                                        <Row alignItems gap="10px">
                                            <ProfilePhoto capitalLetters={`${notification.senders?.[0]?.firstName.charAt(0).toUpperCase()}${notification.senders?.[0]?.lastName.charAt(0).toUpperCase()}`} />
                                            <Text>{notification.type === 'note-create-tag' ? `${sender} created a new note and tagged you` : `${sender} updated the note where you are tagged`}</Text>
                                        </Row>
                                        <Text $lighter>Notes ∙ {moment(notification.createdAt).fromNow()}</Text>
                                    </Column>
                                    <Dot>
                                        {!notification?.read && <SvgFragment type="blueDotNotification" />}
                                    </Dot>
                                </Row>
                            )}
                        </StyledDropdownItem>
                    )
                })}
            </>) : (<>
                <DropdownEmptyState text={"No new notifications yet"} image={ImageNoNotifications} />
            </>)}
       </StyledNotificationsDropdown>
    )
}

const StyledNotificationsDropdown = styled(DropdownMenu)`
    margin-left: -5px;

    .dropdown-toggle,
    .actions-dropdown,
    .dropdownWrapperReference {
        padding: 0;
        color: ${styles.colors.black300};

        &::after {
            display: none !important;
            content: none !important;
        }

        &:hover {
            color: ${styles.colors.black300};
            border: none;

            svg {
                fill: none;
            }

            svg path {
                fill: none;
                stroke: currentColor;
            }
        }
    }
`

const NotificationBellTrigger = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: ${styles.colors.black300};
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    & svg {
        fill: none;
        stroke: currentColor;
    }

    &:hover {
        background: ${styles.colors.black100};
        color: ${styles.colors.black400};

        & svg path {
            fill: none;
            stroke: currentColor;
        }
    }
`

const NotificationCount = styled.span`
    position: absolute;
    top: 0;
    left: 20px;
    z-index: 1;
    min-width: 16px;
    border-radius: 9999px;
    background: red;
    color: ${styles.colors.white};
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
`

const StyledText = styled(Text)`
    width: 470px;
    display: inline-block;
    text-align: left;
    font-weight: normal;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    word-break: break-all;
`;
const Dot = styled.div`
    width: 8px;
    flex-shrink: 0;
`

const RemovalRow = styled(Row)`
    position: relative;
    width: 100%;
`

const RemovalDot = styled(Dot)`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
`

const CloseButton = styled.div`
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    cursor: pointer;

    &:hover {
        & svg path { fill: ${styles.colors.primary600}; }
    }
`

const HoverRow = styled(Row)`
    position: relative;
    &:hover ${CloseButton} {
        display: block;
    }
`

const StyledDropdownItem = styled(DropdownItem)`
    position: relative;
    width: 590px;
`

const StyledProfilePhoto = styled(ProfilePhoto)`
    margin-bottom: auto;
`
const StyledCheckMarkBox = styled.div`
    width: 32px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: auto;
`

const HubspotCheckMark = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ${styles.colors.primary600};
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    margin-bottom: auto;
    .svg {
        width: 16px;
        height: 16px;
        & .path { fill: ${styles.colors.white} !important; }; 
    }

    &:hover .svg .path {
        fill: ${styles.colors.white} !important;
    }
`

const HubspotProcessingMark = styled(HubspotCheckMark)`
    background-color: transparent;

    .svg {
        width: 16px;
        height: 16px;

        .path {
            fill: ${styles.colors.primary600};
        }
    }
`

const HoverSpan = styled.span`
    font-weight: bold;
    cursor: pointer;
    transition: color 0.2s ease;
    
    &:hover {
        color: ${styles.colors.primary600};
    }
`

export default Notifications